import { useRef } from 'react';
import Autoplay, { AutoplayType } from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { mentors } from '../../../data/mentors';
import LinkedInIcon from './LinkedInIcon';

const companyNames = Array.from(new Set(mentors.map((mentor) => mentor.company))).filter(Boolean);
const marqueeCompanies = companyNames.length ? [...companyNames, ...companyNames] : [];

export default function MentorsCatalog() {
  const autoplay = useRef<AutoplayType>(
    Autoplay({
      delay: 2200,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    })
  );
  const [companiesCarouselRef] = useEmblaCarousel(
    {
      loop: true,
      dragFree: true,
      align: 'start',
    },
    [autoplay.current]
  );

  return (
    <section id="mentores" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#1b3d70] mb-4">
            Nuestros Mentores
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Conoce a los expertos de la industria que te guiarán en tu desarrollo profesional
          </p>
          {marqueeCompanies.length > 0 && (
            <div className="mt-10">
              <p className="text-sm text-gray-500 mb-4">
                Nuestros mentores han colaborado en:
              </p>
              <div className="overflow-hidden" ref={companiesCarouselRef}>
                <div className="flex items-center gap-4 sm:gap-6">
                  {marqueeCompanies.map((company, index) => (
                    <div
                      key={`${company}-${index}`}
                      className="flex-none min-w-[130px] sm:min-w-[160px] lg:min-w-[200px]"
                    >
                      <div className="text-center text-sm sm:text-base font-semibold text-gray-900 opacity-60 transition-opacity duration-300 hover:opacity-100">
                        {company}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mentors Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {mentors.map((mentor) => (
            <div
              key={mentor.id}
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-xl group"
            >
              {/* Image */}
              <div className="relative w-full overflow-hidden bg-gray-100 aspect-[4/5] sm:aspect-[3/4] lg:aspect-[4/5]">
                <img
                  src={mentor.image}
                  alt={mentor.name}
                  className="w-full h-full object-cover object-center sm:object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              {/* Content */}
              <div className="p-5 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-[#1b3d70] mb-1 group-hover:text-[#bb8800] transition-colors">
                  {mentor.name}
                </h3>
                <p className="text-sm font-medium text-[#bb8800] mb-1">
                  {mentor.position}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  {mentor.company}
                </p>
                
                <div className="mt-auto">
                  {mentor.linkedin && (
                    <a
                      href={mentor.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block text-gray-400 hover:text-[#0077b5] transition-colors"
                    >
                      <LinkedInIcon />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-12 text-center text-base text-gray-500">
          Y muchos más mentores se unirán pronto.
        </p>
      </div>
    </section>
  );
}